import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesServicioAPI, ordenesCatalogosAPI, ordenesDocumentosAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    ArrowLeft,
    ClipboardList,
    Car,
    Users,
    Package,
    Clock,
    CheckCircle,
    DollarSign,
    Wrench,
    AlertCircle,
    Play,
    FileText,
    XCircle,
    Loader2,
    Calendar,
    User,
    MapPin,
    Phone,
    Plus,
    Trash2,
    History,
    Edit,
    Send,
    Mail,
    ExternalLink,
    PenLine,
    Upload,
    Image,
    File,
    Download,
    Eye,
} from 'lucide-react';

const ESTADO_CONFIG = {
    pendiente: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock, label: 'Pendiente', next: 'Tomar Orden' },
    esperando_cliente: { color: 'bg-orange-100 text-orange-700 border-orange-300', icon: Mail, label: 'Esperando Cliente', next: null },
    pendiente_validacion: { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: CheckCircle, label: 'Pendiente Validación', next: 'Validar' },
    asignada: { color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: Clock, label: 'Asignada', next: 'Iniciar Trabajo' },
    en_proceso: { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Wrench, label: 'En Proceso', next: 'Completar' },
    realizada: { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: CheckCircle, label: 'Realizada', next: 'Facturar' },
    facturada: { color: 'bg-green-100 text-green-700 border-green-300', icon: DollarSign, label: 'Facturada', next: null },
    cancelada: { color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle, label: 'Cancelada', next: null },
};

const TIPOS_DISPOSITIVO = {
    gps: 'GPS',
    sensor_fatiga: 'Sensor Fatiga/Somnolencia',
    camara_frontal: 'Cámara Frontal',
    camara_interior: 'Cámara Interior',
    sensor_combustible: 'Sensor Combustible',
    boton_panico: 'Botón Pánico',
    inmovilizador: 'Inmovilizador',
};

export default function OrdenDetallePage() {
    const { ordenId } = useParams();
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [orden, setOrden] = useState(null);
    const [tiposServicio, setTiposServicio] = useState([]);
    const [processing, setProcessing] = useState(false);
    
    // Dialogs
    const [showTomarDialog, setShowTomarDialog] = useState(false);
    const [showCompletarDialog, setShowCompletarDialog] = useState(false);
    const [showFacturarDialog, setShowFacturarDialog] = useState(false);
    const [showCancelarDialog, setShowCancelarDialog] = useState(false);
    const [showEnviarClienteDialog, setShowEnviarClienteDialog] = useState(false);
    const [showValidarDialog, setShowValidarDialog] = useState(false);
    const [enlaceCliente, setEnlaceCliente] = useState(null);
    const [validarObs, setValidarObs] = useState('');
    
    // Form data
    const [tomarObs, setTomarObs] = useState('');
    const [completarData, setCompletarData] = useState({
        trabajo_realizado: '',
        observaciones: '',
        dispositivos: [],
    });
    const [facturarData, setFacturarData] = useState({
        numero_factura: '',
        monto: '',
        fecha_factura: '',
        observaciones: '',
    });
    const [cancelarMotivo, setCancelarMotivo] = useState('');
    
    // Firma canvas
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    
    // Documentos
    const [documentos, setDocumentos] = useState([]);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [tipoDocumento, setTipoDocumento] = useState('foto_trabajo');
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadData();
    }, [ordenId]);
    
    useEffect(() => {
        if (ordenId) {
            loadDocumentos();
        }
    }, [ordenId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [ordenRes, tiposRes] = await Promise.all([
                ordenesServicioAPI.getById(ordenId),
                ordenesCatalogosAPI.getTiposServicio(),
            ]);
            setOrden(ordenRes.data);
            setTiposServicio(tiposRes.data);
        } catch (err) {
            toast.error('Error al cargar la orden');
            navigate('/ordenes/ordenes');
        } finally {
            setLoading(false);
        }
    };
    
    const loadDocumentos = async () => {
        try {
            setLoadingDocs(true);
            const res = await ordenesDocumentosAPI.getByOrden(ordenId);
            setDocumentos(res.data);
        } catch (err) {
            console.error('Error al cargar documentos:', err);
        } finally {
            setLoadingDocs(false);
        }
    };
    
    const handleUploadDoc = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validar tamaño (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('El archivo es demasiado grande. Máximo 10MB');
            return;
        }
        
        setUploadingDoc(true);
        try {
            const res = await ordenesDocumentosAPI.upload(ordenId, file, tipoDocumento);
            toast.success(res.data.mensaje_compresion || 'Documento subido exitosamente');
            loadDocumentos();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al subir documento');
        } finally {
            setUploadingDoc(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    
    const handleDeleteDoc = async (docId) => {
        if (!window.confirm('¿Está seguro de eliminar este documento?')) return;
        
        try {
            await ordenesDocumentosAPI.delete(docId);
            toast.success('Documento eliminado');
            loadDocumentos();
        } catch (err) {
            toast.error('Error al eliminar documento');
        }
    };
    
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const handleTomar = async () => {
        setProcessing(true);
        try {
            await ordenesServicioAPI.tomar(ordenId, { observaciones: tomarObs });
            toast.success('Orden tomada exitosamente');
            setShowTomarDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al tomar la orden');
        } finally {
            setProcessing(false);
        }
    };

    const handleEnviarCliente = async (conEmail = false) => {
        setProcessing(true);
        try {
            const res = await ordenesServicioAPI.enviarCliente(ordenId, conEmail);
            setEnlaceCliente(res.data);
            if (conEmail && res.data.email_enviado) {
                toast.success('Enlace enviado por email');
            } else {
                toast.success('Enlace generado');
            }
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al generar enlace');
        } finally {
            setProcessing(false);
        }
    };

    const handleValidarCliente = async (aprobado) => {
        setProcessing(true);
        try {
            await ordenesServicioAPI.validarCliente(ordenId, aprobado, validarObs);
            toast.success(aprobado ? 'Orden validada y lista para técnico' : 'Orden rechazada');
            setShowValidarDialog(false);
            setValidarObs('');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al validar');
        } finally {
            setProcessing(false);
        }
    };

    const handleCompletar = async () => {
        if (!completarData.trabajo_realizado) {
            toast.error('Debe describir el trabajo realizado');
            return;
        }
        
        setProcessing(true);
        try {
            // Get signature if canvas has content
            let firma = null;
            if (canvasRef.current) {
                const ctx = canvasRef.current.getContext('2d');
                const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
                const hasContent = imageData.data.some((channel, i) => i % 4 !== 3 && channel !== 255);
                if (hasContent) {
                    firma = canvasRef.current.toDataURL('image/png');
                }
            }
            
            await ordenesServicioAPI.completar(ordenId, {
                trabajo_realizado: completarData.trabajo_realizado,
                observaciones: completarData.observaciones,
                dispositivos_instalados: completarData.dispositivos,
                firma_cliente: firma,
            });
            toast.success('Orden completada exitosamente');
            setShowCompletarDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al completar la orden');
        } finally {
            setProcessing(false);
        }
    };

    const handleFacturar = async () => {
        if (!facturarData.numero_factura) {
            toast.error('Debe ingresar el número de factura');
            return;
        }
        
        setProcessing(true);
        try {
            await ordenesServicioAPI.facturar(ordenId, {
                numero_factura: facturarData.numero_factura,
                monto: facturarData.monto ? parseFloat(facturarData.monto) : null,
                fecha_factura: facturarData.fecha_factura || null,
                observaciones: facturarData.observaciones,
            });
            toast.success('Orden facturada exitosamente');
            setShowFacturarDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al facturar la orden');
        } finally {
            setProcessing(false);
        }
    };

    const handleCancelar = async () => {
        if (!cancelarMotivo) {
            toast.error('Debe indicar el motivo de cancelación');
            return;
        }
        
        setProcessing(true);
        try {
            await ordenesServicioAPI.cancelar(ordenId, cancelarMotivo);
            toast.success('Orden cancelada');
            setShowCancelarDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cancelar la orden');
        } finally {
            setProcessing(false);
        }
    };

    const addDispositivo = () => {
        setCompletarData({
            ...completarData,
            dispositivos: [...completarData.dispositivos, { tipo_dispositivo: '', serial: '', marca: '', modelo: '' }]
        });
    };

    const removeDispositivo = (index) => {
        setCompletarData({
            ...completarData,
            dispositivos: completarData.dispositivos.filter((_, i) => i !== index)
        });
    };

    const updateDispositivo = (index, field, value) => {
        const updated = [...completarData.dispositivos];
        updated[index][field] = value;
        setCompletarData({ ...completarData, dispositivos: updated });
    };

    // Canvas drawing
    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        
        // Soporte para touch y mouse
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        ctx.beginPath();
        ctx.moveTo(clientX - rect.left, clientY - rect.top);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        setIsDrawing(true);
        
        // Prevenir scroll en móviles mientras se dibuja
        if (e.touches) e.preventDefault();
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        
        // Soporte para touch y mouse
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        
        ctx.lineTo(clientX - rect.left, clientY - rect.top);
        ctx.stroke();
        
        // Prevenir scroll en móviles mientras se dibuja
        if (e.touches) e.preventDefault();
    };

    const stopDrawing = () => setIsDrawing(false);

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };
    
    // Inicializar canvas con fondo blanco cuando se abre el diálogo
    useEffect(() => {
        if (showCompletarDialog && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
    }, [showCompletarDialog]);

    if (loading || permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!orden) return null;

    const estadoConfig = ESTADO_CONFIG[orden.estado] || ESTADO_CONFIG.pendiente;
    const Icon = estadoConfig.icon;
    const tipoServicioNombre = tiposServicio.find(t => t.codigo === orden.tipo_servicio)?.nombre || orden.tipo_servicio;

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/ordenes')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <ClipboardList className="h-6 w-6" />
                            {orden.numero_orden}
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <Badge className={estadoConfig.color}>
                                <Icon className="h-3 w-3 mr-1" />
                                {estadoConfig.label}
                            </Badge>
                            <Badge variant="outline">{tipoServicioNombre}</Badge>
                        </div>
                    </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                    {orden.estado === 'pendiente' && tienePermiso('tomar_ordenes') && (
                        <Button onClick={() => setShowTomarDialog(true)}>
                            <Play className="h-4 w-4 mr-2" />
                            Tomar Orden
                        </Button>
                    )}
                    {orden.estado === 'pendiente' && tienePermiso('crear_ordenes') && (
                        <Button variant="outline" onClick={() => setShowEnviarClienteDialog(true)}>
                            <Mail className="h-4 w-4 mr-2" />
                            Enviar al Cliente
                        </Button>
                    )}
                    {orden.estado === 'esperando_cliente' && (
                        <Badge className="bg-orange-100 text-orange-700 border-orange-300 py-2 px-3">
                            <Clock className="h-4 w-4 mr-2" />
                            Esperando datos del cliente
                        </Badge>
                    )}
                    {orden.estado === 'pendiente_validacion' && tienePermiso('crear_ordenes') && (
                        <Button onClick={() => setShowValidarDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Validar Datos del Cliente
                        </Button>
                    )}
                    {orden.estado === 'en_proceso' && tienePermiso('completar_ordenes') && (
                        <Button onClick={() => setShowCompletarDialog(true)}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Completar
                        </Button>
                    )}
                    {orden.estado === 'realizada' && tienePermiso('facturar_ordenes') && (
                        <Button onClick={() => setShowFacturarDialog(true)} className="bg-green-600 hover:bg-green-700">
                            <DollarSign className="h-4 w-4 mr-2" />
                            Facturar
                        </Button>
                    )}
                    {!['facturada', 'cancelada'].includes(orden.estado) && tienePermiso('cancelar_ordenes') && (
                        <Button variant="outline" className="text-red-600" onClick={() => setShowCancelarDialog(true)}>
                            <XCircle className="h-4 w-4 mr-2" />
                            Cancelar
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Cliente */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                Cliente
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {orden.cliente ? (
                                <div className="space-y-2">
                                    <p className="font-bold text-lg">{orden.cliente.razon_social}</p>
                                    <p className="font-mono text-muted-foreground">{orden.cliente.rut}</p>
                                    {orden.cliente.telefono && (
                                        <p className="flex items-center gap-2 text-sm">
                                            <Phone className="h-4 w-4" /> {orden.cliente.telefono}
                                        </p>
                                    )}
                                    <Button variant="link" className="p-0 h-auto" onClick={() => navigate(`/ordenes/clientes/${orden.cliente_id}`)}>
                                        Ver ficha del cliente
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-muted-foreground">Sin cliente asignado</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Vehículo */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Car className="h-5 w-5" />
                                Vehículo
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {orden.vehiculo ? (
                                <div className="space-y-2">
                                    <p className="font-bold text-2xl font-mono">{orden.vehiculo.patente}</p>
                                    <p>{orden.vehiculo.marca} {orden.vehiculo.modelo} {orden.vehiculo.anio}</p>
                                    <p className="text-sm text-muted-foreground">{orden.vehiculo.tipo_vehiculo} - {orden.vehiculo.color}</p>
                                </div>
                            ) : (
                                <p className="text-muted-foreground">Sin vehículo asignado</p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Descripción */}
                    {(orden.descripcion || orden.observaciones) && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Descripción del Trabajo
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {orden.descripcion && <p>{orden.descripcion}</p>}
                                {orden.observaciones && (
                                    <div className="bg-muted p-3 rounded">
                                        <p className="text-sm font-medium mb-1">Observaciones:</p>
                                        <p className="text-sm">{orden.observaciones}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Trabajo Realizado */}
                    {orden.trabajo_realizado && (
                        <Card className="border-green-200 bg-green-50/50">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-green-700">
                                    <CheckCircle className="h-5 w-5" />
                                    Trabajo Realizado
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p>{orden.trabajo_realizado}</p>
                                {orden.observaciones_cierre && (
                                    <p className="mt-2 text-sm text-muted-foreground">{orden.observaciones_cierre}</p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Dispositivos Instalados */}
                    {orden.dispositivos_instalados?.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Package className="h-5 w-5" />
                                    Dispositivos Instalados
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {orden.dispositivos_instalados.map((d, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 bg-muted rounded">
                                            <div>
                                                <p className="font-medium">{TIPOS_DISPOSITIVO[d.tipo_dispositivo] || d.tipo_dispositivo}</p>
                                                <p className="text-sm text-muted-foreground">Serial: {d.serial || 'N/A'}</p>
                                            </div>
                                            <Badge>{d.marca} {d.modelo}</Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Factura */}
                    {orden.estado === 'facturada' && (
                        <Card className="border-green-200 bg-green-50/50">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-green-700">
                                    <DollarSign className="h-5 w-5" />
                                    Información de Facturación
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-muted-foreground">N° Factura</p>
                                        <p className="font-bold">{orden.numero_factura}</p>
                                    </div>
                                    {orden.monto_factura && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Monto</p>
                                            <p className="font-bold">${orden.monto_factura.toLocaleString('es-CL')}</p>
                                        </div>
                                    )}
                                    {orden.fecha_factura && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Fecha Factura</p>
                                            <p>{new Date(orden.fecha_factura).toLocaleDateString('es-CL')}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Info Card */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-sm">Información</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Prioridad</span>
                                <Badge variant={orden.prioridad === 'alta' ? 'destructive' : 'secondary'}>
                                    {orden.prioridad}
                                </Badge>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Creada</span>
                                <span>{new Date(orden.created_at).toLocaleDateString('es-CL')}</span>
                            </div>
                            {orden.fecha_programada && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Programada</span>
                                    <span>{new Date(orden.fecha_programada).toLocaleDateString('es-CL')}</span>
                                </div>
                            )}
                            {orden.tecnico && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Técnico</span>
                                    <span>{orden.tecnico.nombre}</span>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Historial */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-sm">
                                <History className="h-4 w-4" />
                                Historial
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {orden.historial?.map((h, i) => (
                                    <div key={i} className="relative pl-4 border-l-2 border-muted pb-3 last:pb-0">
                                        <div className="absolute -left-1.5 top-0 w-3 h-3 rounded-full bg-primary" />
                                        <p className="font-medium text-sm">{ESTADO_CONFIG[h.estado]?.label || h.estado}</p>
                                        <p className="text-xs text-muted-foreground">{h.usuario_nombre}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {new Date(h.fecha).toLocaleString('es-CL')}
                                        </p>
                                        {h.comentario && <p className="text-xs mt-1">{h.comentario}</p>}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Firma */}
                    {orden.firma_cliente && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-sm">
                                    <PenLine className="h-4 w-4" />
                                    Firma Cliente
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <img src={orden.firma_cliente} alt="Firma del cliente" className="border rounded" />
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
            
            {/* Documentos Section - Full Width */}
            <Card className="mt-6" data-testid="documentos-section">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <File className="h-5 w-5" />
                            Documentos y Evidencias
                            {documentos.length > 0 && (
                                <Badge variant="secondary">{documentos.length}</Badge>
                            )}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                                <SelectTrigger className="w-[160px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="foto_trabajo">Foto de Trabajo</SelectItem>
                                    <SelectItem value="factura">Factura</SelectItem>
                                    <SelectItem value="evidencia">Evidencia</SelectItem>
                                    <SelectItem value="otro">Otro</SelectItem>
                                </SelectContent>
                            </Select>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx"
                                className="hidden"
                                onChange={handleUploadDoc}
                            />
                            <Button 
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingDoc}
                                data-testid="upload-doc-btn"
                            >
                                {uploadingDoc ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <Upload className="h-4 w-4 mr-2" />
                                )}
                                Subir Archivo
                            </Button>
                        </div>
                    </div>
                    <CardDescription>
                        Sube fotos, facturas o documentos relacionados con esta orden. Las imágenes se comprimen automáticamente.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {loadingDocs ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : documentos.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <File className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>No hay documentos adjuntos</p>
                            <p className="text-sm">Sube fotos del trabajo realizado o documentos relacionados</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {documentos.map((doc) => (
                                <div 
                                    key={doc.id} 
                                    className="border rounded-lg p-3 hover:bg-muted/50 transition-colors"
                                    data-testid={`doc-${doc.id}`}
                                >
                                    {/* Preview de imagen */}
                                    {doc.es_imagen && doc.url_preview && (
                                        <div className="aspect-video bg-muted rounded mb-2 overflow-hidden">
                                            <img 
                                                src={`${process.env.REACT_APP_BACKEND_URL}${doc.url_preview}`}
                                                alt={doc.nombre_original}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                    
                                    {/* Icono para no-imágenes */}
                                    {!doc.es_imagen && (
                                        <div className="aspect-video bg-muted rounded mb-2 flex items-center justify-center">
                                            <FileText className="h-12 w-12 text-muted-foreground" />
                                        </div>
                                    )}
                                    
                                    {/* Info */}
                                    <div className="space-y-1">
                                        <p className="font-medium text-sm truncate" title={doc.nombre_original}>
                                            {doc.nombre_original}
                                        </p>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <Badge variant="outline" className="text-xs">
                                                {doc.tipo}
                                            </Badge>
                                            <span>{formatFileSize(doc.tamano)}</span>
                                            {doc.comprimido && (
                                                <span className="text-green-600">comprimido</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {new Date(doc.created_at).toLocaleDateString('es-CL')}
                                        </p>
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex gap-1 mt-2">
                                        {doc.es_imagen && doc.url_preview && (
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.url_preview}`, '_blank')}
                                            >
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        )}
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.url_descarga}`, '_blank')}
                                        >
                                            <Download className="h-4 w-4" />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={() => handleDeleteDoc(doc.id)}
                                            className="text-red-500 hover:text-red-600"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Tomar Dialog */}
            <Dialog open={showTomarDialog} onOpenChange={setShowTomarDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Tomar Orden</DialogTitle>
                        <DialogDescription>
                            ¿Desea tomar esta orden para trabajar en ella?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Observaciones (opcional)</Label>
                            <Textarea
                                value={tomarObs}
                                onChange={(e) => setTomarObs(e.target.value)}
                                placeholder="Agregar observaciones..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowTomarDialog(false)} disabled={processing}>
                            Cancelar
                        </Button>
                        <Button onClick={handleTomar} disabled={processing}>
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Tomar Orden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Completar Dialog */}
            <Dialog open={showCompletarDialog} onOpenChange={setShowCompletarDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Completar Orden</DialogTitle>
                        <DialogDescription>
                            Registre el trabajo realizado y los dispositivos instalados
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                        <div className="space-y-2">
                            <Label>Trabajo Realizado *</Label>
                            <Textarea
                                value={completarData.trabajo_realizado}
                                onChange={(e) => setCompletarData({...completarData, trabajo_realizado: e.target.value})}
                                placeholder="Describa el trabajo realizado..."
                                rows={3}
                            />
                        </div>

                        {/* Dispositivos */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label>Dispositivos Instalados</Label>
                                <Button type="button" variant="outline" size="sm" onClick={addDispositivo}>
                                    <Plus className="h-4 w-4 mr-1" /> Agregar
                                </Button>
                            </div>
                            {completarData.dispositivos.map((d, i) => (
                                <div key={i} className="grid grid-cols-4 gap-2 p-3 bg-muted rounded items-end">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Tipo</Label>
                                        <Select value={d.tipo_dispositivo} onValueChange={(v) => updateDispositivo(i, 'tipo_dispositivo', v)}>
                                            <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(TIPOS_DISPOSITIVO).map(([k, v]) => (
                                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Serial</Label>
                                        <Input value={d.serial} onChange={(e) => updateDispositivo(i, 'serial', e.target.value)} placeholder="Serial" />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Marca</Label>
                                        <Input value={d.marca} onChange={(e) => updateDispositivo(i, 'marca', e.target.value)} placeholder="Marca" />
                                    </div>
                                    <div className="flex items-end gap-1">
                                        <Input value={d.modelo} onChange={(e) => updateDispositivo(i, 'modelo', e.target.value)} placeholder="Modelo" />
                                        <Button type="button" variant="ghost" size="icon" onClick={() => removeDispositivo(i)}>
                                            <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Firma */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Firma del Cliente</Label>
                                <Button type="button" variant="ghost" size="sm" onClick={clearCanvas}>
                                    Limpiar
                                </Button>
                            </div>
                            <canvas
                                ref={canvasRef}
                                width={400}
                                height={150}
                                className="border rounded cursor-crosshair bg-white w-full touch-none"
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                            <p className="text-xs text-muted-foreground">Dibuje la firma del cliente en el recuadro</p>
                        </div>

                        <div className="space-y-2">
                            <Label>Observaciones (opcional)</Label>
                            <Textarea
                                value={completarData.observaciones}
                                onChange={(e) => setCompletarData({...completarData, observaciones: e.target.value})}
                                placeholder="Observaciones adicionales..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCompletarDialog(false)} disabled={processing}>
                            Cancelar
                        </Button>
                        <Button onClick={handleCompletar} disabled={processing}>
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Completar Orden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Facturar Dialog */}
            <Dialog open={showFacturarDialog} onOpenChange={setShowFacturarDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Facturar Orden</DialogTitle>
                        <DialogDescription>
                            Ingrese los datos de la factura
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>N° Factura *</Label>
                            <Input
                                value={facturarData.numero_factura}
                                onChange={(e) => setFacturarData({...facturarData, numero_factura: e.target.value})}
                                placeholder="F-00001"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Monto</Label>
                            <Input
                                type="number"
                                value={facturarData.monto}
                                onChange={(e) => setFacturarData({...facturarData, monto: e.target.value})}
                                placeholder="100000"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Fecha Factura</Label>
                            <Input
                                type="date"
                                value={facturarData.fecha_factura}
                                onChange={(e) => setFacturarData({...facturarData, fecha_factura: e.target.value})}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Observaciones</Label>
                            <Textarea
                                value={facturarData.observaciones}
                                onChange={(e) => setFacturarData({...facturarData, observaciones: e.target.value})}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowFacturarDialog(false)} disabled={processing}>
                            Cancelar
                        </Button>
                        <Button onClick={handleFacturar} disabled={processing} className="bg-green-600 hover:bg-green-700">
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Facturar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancelar Dialog */}
            <Dialog open={showCancelarDialog} onOpenChange={setShowCancelarDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Cancelar Orden</DialogTitle>
                        <DialogDescription>
                            Esta acción no se puede deshacer. La orden quedará marcada como cancelada.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Motivo de Cancelación *</Label>
                            <Textarea
                                value={cancelarMotivo}
                                onChange={(e) => setCancelarMotivo(e.target.value)}
                                placeholder="Indique el motivo de la cancelación..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCancelarDialog(false)} disabled={processing}>
                            Volver
                        </Button>
                        <Button variant="destructive" onClick={handleCancelar} disabled={processing || !cancelarMotivo}>
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Cancelar Orden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Enviar al Cliente */}
            <Dialog open={showEnviarClienteDialog} onOpenChange={setShowEnviarClienteDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Send className="h-5 w-5" />
                            Enviar al Cliente para Completar
                        </DialogTitle>
                        <DialogDescription>
                            Genera un enlace para que el cliente complete los datos faltantes. El envío es opcional.
                        </DialogDescription>
                    </DialogHeader>
                    {!enlaceCliente ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <p className="text-sm"><strong>Cliente:</strong> {orden?.cliente?.razon_social}</p>
                                <p className="text-sm"><strong>Email:</strong> {orden?.cliente?.email || 'No configurado'}</p>
                                <p className="text-sm"><strong>Teléfono:</strong> {orden?.cliente?.telefono || 'No configurado'}</p>
                            </div>
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                                <p className="font-medium mb-1">El cliente podrá completar:</p>
                                <ul className="list-disc list-inside text-muted-foreground">
                                    <li>Datos del vehículo (si no fueron ingresados)</li>
                                    <li>Ubicación donde se realizará el servicio</li>
                                    <li>Fecha y hora preferida</li>
                                    <li>Observaciones adicionales</li>
                                </ul>
                                <p className="mt-2 text-orange-600">⏱️ El enlace expirará en 24 horas.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-green-700 font-medium flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5" />
                                    Enlace generado
                                </p>
                                {enlaceCliente.email_enviado && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        ✉️ Email enviado a: {enlaceCliente.email_destino}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label>Enlace para el cliente:</Label>
                                <div className="flex gap-2">
                                    <Input value={enlaceCliente.enlace} readOnly className="text-xs" />
                                    <Button 
                                        variant="outline" 
                                        size="icon"
                                        onClick={() => {
                                            navigator.clipboard.writeText(enlaceCliente.enlace);
                                            toast.success('Enlace copiado');
                                        }}
                                        title="Copiar enlace"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Opciones de envío */}
                            <div className="border-t pt-4">
                                <p className="text-sm font-medium mb-3">Enviar enlace al cliente (opcional):</p>
                                <div className="flex flex-col gap-2">
                                    {orden?.cliente?.telefono && (
                                        <Button 
                                            variant="outline" 
                                            className="justify-start bg-green-50 hover:bg-green-100 border-green-300"
                                            onClick={() => {
                                                const phone = orden.cliente.telefono.replace(/[^0-9]/g, '');
                                                const phoneFormatted = phone.startsWith('56') ? phone : `56${phone}`;
                                                const message = encodeURIComponent(
                                                    `Hola ${orden.cliente.razon_social},\n\n` +
                                                    `Le enviamos el enlace para completar su orden de servicio ${orden.numero_orden}:\n\n` +
                                                    `🔗 ${enlaceCliente.enlace}\n\n` +
                                                    `Por favor complete los datos antes de 24 horas.\n\n` +
                                                    `Servicios 4Sat`
                                                );
                                                window.open(`https://wa.me/${phoneFormatted}?text=${message}`, '_blank');
                                            }}
                                        >
                                            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#25D366">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                            Enviar por WhatsApp
                                        </Button>
                                    )}
                                    {!enlaceCliente.email_enviado && orden?.cliente?.email && (
                                        <Button 
                                            variant="outline" 
                                            className="justify-start"
                                            onClick={() => handleEnviarCliente(true)}
                                            disabled={processing}
                                        >
                                            {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                                            Enviar por Email
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        {!enlaceCliente ? (
                            <>
                                <Button variant="outline" onClick={() => setShowEnviarClienteDialog(false)} disabled={processing}>
                                    Cancelar
                                </Button>
                                <Button onClick={() => handleEnviarCliente(false)} disabled={processing}>
                                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                                    Generar Enlace
                                </Button>
                            </>
                        ) : (
                            <Button onClick={() => { setShowEnviarClienteDialog(false); setEnlaceCliente(null); }}>
                                Cerrar
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Validar Datos del Cliente */}
            <Dialog open={showValidarDialog} onOpenChange={setShowValidarDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-purple-600" />
                            Validar Datos del Cliente
                        </DialogTitle>
                        <DialogDescription>
                            Revisa los datos ingresados por el cliente y decide si aprobar o rechazar.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                        {/* Datos del vehículo completados por cliente */}
                        {orden?.vehiculo_patente && (
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <p className="font-medium text-sm mb-2">Vehículo</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <span className="text-muted-foreground">Patente:</span>
                                    <span className="font-mono">{orden.vehiculo_patente}</span>
                                    {orden.vehiculo_marca && <>
                                        <span className="text-muted-foreground">Marca:</span>
                                        <span>{orden.vehiculo_marca}</span>
                                    </>}
                                    {orden.vehiculo_modelo && <>
                                        <span className="text-muted-foreground">Modelo:</span>
                                        <span>{orden.vehiculo_modelo}</span>
                                    </>}
                                </div>
                            </div>
                        )}
                        
                        {/* Ubicación del servicio */}
                        {orden?.ubicacion_servicio && (
                            <div className="p-3 bg-orange-50 rounded-lg">
                                <p className="font-medium text-sm mb-2 flex items-center gap-2">
                                    <MapPin className="h-4 w-4" />
                                    Ubicación del Servicio
                                </p>
                                <div className="text-sm space-y-1">
                                    <p>{orden.ubicacion_servicio.direccion}</p>
                                    {orden.ubicacion_servicio.comuna && (
                                        <p className="text-muted-foreground">{orden.ubicacion_servicio.comuna}, {orden.ubicacion_servicio.ciudad}</p>
                                    )}
                                    {orden.ubicacion_servicio.referencias && (
                                        <p className="text-muted-foreground italic">"{orden.ubicacion_servicio.referencias}"</p>
                                    )}
                                    {orden.ubicacion_servicio.latitud && orden.ubicacion_servicio.longitud && (
                                        <a 
                                            href={`https://www.google.com/maps?q=${orden.ubicacion_servicio.latitud},${orden.ubicacion_servicio.longitud}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-500 underline text-xs"
                                        >
                                            Ver en Google Maps
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Fecha preferida */}
                        {(orden?.fecha_preferida_cliente || orden?.hora_preferida_cliente) && (
                            <div className="p-3 bg-green-50 rounded-lg">
                                <p className="font-medium text-sm mb-1">Preferencia del Cliente</p>
                                <p className="text-sm">
                                    {orden.fecha_preferida_cliente && `Fecha: ${orden.fecha_preferida_cliente}`}
                                    {orden.hora_preferida_cliente && ` - Hora: ${orden.hora_preferida_cliente}`}
                                </p>
                            </div>
                        )}
                        
                        {/* Observaciones del cliente */}
                        {orden?.observaciones_cliente && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="font-medium text-sm mb-1">Observaciones del Cliente</p>
                                <p className="text-sm text-muted-foreground">{orden.observaciones_cliente}</p>
                            </div>
                        )}
                        
                        {/* Campo para observaciones de validación */}
                        <div className="space-y-2">
                            <Label>Observaciones (opcional, requerido si rechaza)</Label>
                            <Textarea
                                value={validarObs}
                                onChange={(e) => setValidarObs(e.target.value)}
                                placeholder="Ej: Falta especificar número de departamento..."
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                            variant="outline" 
                            className="text-red-600 border-red-200"
                            onClick={() => handleValidarCliente(false)}
                            disabled={processing}
                        >
                            <XCircle className="h-4 w-4 mr-2" />
                            Rechazar y Pedir Corrección
                        </Button>
                        <Button 
                            onClick={() => handleValidarCliente(true)}
                            disabled={processing}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                            Aprobar y Continuar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
